import { takeLatest, takeEvery, put, call, select } from 'redux-saga/effects';
import { initialize } from 'redux-form';
import { delay } from 'redux-saga';

import { GetSupplierListResponseDto } from '@ce-openapi/purchase-order-view-service-api-documentation-ts-axios-client';
import { SupplierDto } from '@ce-openapi/purchase-order-view-service-api-documentation-ts-axios-client/ce-openapi/purchase_order_view_service_api_documentation/openapi/model/supplier-dto';
import * as act from '../../actions';
import {
  INVENTORY_SUPPLIER_PAGE_REQUEST,
  INVENTORY_SUPPLIER_SUPPLIERS_REQUEST,
  INVENTORY_SUPPLIER_ON_CHANGE_KEYWORD,
  INVENTORY_SUPPLIER_EXPORT_SUPPLIER,
  INVENTORY_SUPPLIER_ON_CLICK_ROW,
  INVENTORY_SUPPLIER_ASK_DELETE_SUPPLIER,
  INVENTORY_SUPPLIER_CANCEL_DELETE_SUPPLIER,
  INVENTORY_SUPPLIER_DELETE_SUPPLIER,
  INVENTORY_SUPPLIER_ON_CLICK_CANCEL,
  INVENTORY_SUPPLIER_ON_CLICK_CREATE_SUPPLIER,
  INVENTORY_SUPPLIER_SUBMIT_FORM_CREATE,
  INVENTORY_SUPPLIER_SUBMIT_FORM_UPDATE,
  INVENTORY_SUPPLIER_EXPORT_FAILED,
  INVENTORY_SUPPLIER_SUPPLIERS_LOAD_MORE,
} from '../../actions/inventory/supplier/constants';
import * as actSupplier from '../../actions/inventory/supplier';
import * as actNotif from '../../actions/notifications';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';

import * as apiInvSupplier from '../../api/inventory/suppliers';
import * as apiOutlets from '../../api/outlets';
import * as apiProfile from '../../api/profile';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapInventorySuppliers from '../../clevertap/inventory/suppliers';
import { unleashHasFeature } from '../../utils/featureFlags';

import {
  getSupplierListArray,
  getFiltersFormValues,
} from '../../selectors/inventory/supplier';

export function* initializeForm(supplier) {
  yield put(
    initialize('supplier', {
      id: supplier.id,
      name: supplier.name,
      address: supplier.address,
      city: supplier.city,
      email: supplier.email,
      phone: supplier.phone,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      postal_code: supplier.postal_code,
      state: supplier.state,
    })
  );
}

export function* emptyForm() {
  yield put(initialize('supplier', {}));
}

export function* pageRequestFlow() {
  // reset...
  yield put(actSupplier.setSuppliers([]));
  // set false by suppliers request saga
  yield put(showPageLoadingSpinner(true));
  yield put(actSupplier.setSupplierDetail(null));
  yield put(actSupplier.closeRightColumn());

  yield put(act.showProgressBar());
  try {
    const response = yield call(apiOutlets.outlets);
    yield put(actSupplier.setOutlets(response.data));

    const resProfile = yield call(apiProfile.getProfile);
    yield put(actSupplier.setProfile(resProfile.data));

    yield put(actSupplier.suppliersRequest({}));

    yield put(
      initialize('search', {
        keyword: '',
      })
    );
    clevertapInventorySuppliers.inventorySupplierStart();
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* suppliersRequest(request) {
  const {
    payload: { params },
  } = request;
  yield put(act.showProgressBar());
  try {
    const { isEnabled: isUsingPurchaseOrderAPI } = yield select(
      unleashHasFeature('enable-supplier-bo-read-purchase-order')
    );
    if (isUsingPurchaseOrderAPI) {
      const { data } = yield call(
        apiInvSupplier.getPurchaseOrderSuppliers,
        params
      );

      yield call(setSupplierListState, data);
    } else {
      const resSuppliers = yield call(apiInvSupplier.getSuppliers, params);
      const suppliers = resSuppliers.data;

      yield put(actSupplier.setSuppliers(suppliers));
    }
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

type LoadMoreSuppliersRequestPayload = {
  params?: string;
};

type LoadMoreSuppliersRequest = {
  type?: string;
  payload?: LoadMoreSuppliersRequestPayload;
};

export function* loadMoreSuppliers(request: LoadMoreSuppliersRequest) {
  const {
    payload: { params },
  } = request;
  const cursor = params;
  yield put(act.showProgressBar());
  try {
    const { isEnabled: isUsingPurchaseOrderAPI } = yield select(
      unleashHasFeature('enable-supplier-bo-read-purchase-order')
    );
    if (!isUsingPurchaseOrderAPI) {
      return; // jump to finally block
    }
    const currentList = yield select(getSupplierListArray);
    const filters = yield select(getFiltersFormValues);
    const { data } = yield call(apiInvSupplier.getPurchaseOrderSuppliers, {
      cursor,
      keyword: filters.keyword,
    });

    if (data.next_cursor !== '' && data.nextCursor !== cursor) {
      yield call(setSupplierListState, data, currentList);
    }
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* setSupplierListState(
  responseData: GetSupplierListResponseDto,
  prependWith: SupplierDto[] = []
) {
  const { suppliers, next_cursor } = responseData;
  yield put(actSupplier.setSuppliers(prependWith.concat(suppliers)));
  yield put(actSupplier.setNextCursor(next_cursor || ''));
}

export function* onChangeKeyword(request) {
  yield put(actSupplier.setSuppliers([]));
  // set false by suppliers request saga
  yield put(showPageLoadingSpinner(true));
  const {
    payload: { keyword },
  } = request;
  // delay to prevent too fast ajax request
  yield call(delay, 300);
  yield put(actSupplier.suppliersRequest({ keyword }));
}

export function* exportSupplier(request) {
  yield put(act.showLoadingSpinner(true));
  try {
    yield put(actSupplier.startExportSupplier());
    const { email, keyword } = request.payload.params;
    const { data } = yield call(
      apiInvSupplier.getExportSupplier,
      email,
      keyword
    );
    yield put(actSupplier.setIdAsyncJob(data));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actSupplier.doneExportSupplier());
    yield put(act.showLoadingSpinner(false));
    yield put(actNotif.error(error.message));
  }
}

export function* exportSupplierFailed(error) {
  yield put(act.showLoadingSpinner(false));
  yield put(actNotif.error(error.message));
}

export function* onClickRow(request) {
  const {
    payload: { supplier },
  } = request;
  clevertapInventorySuppliers.inventorySupplierSelect({ supplier });
  yield put(actSupplier.setSupplierDetail(supplier));
  yield put(actSupplier.openRightColumn());
  yield call(initializeForm, supplier);
}

export function* onClickCancel() {
  yield put(actSupplier.setSupplierDetail(null));
  yield call(emptyForm);
  yield put(actSupplier.closeRightColumn());
}

export function* askDeleteSupplier() {
  yield put(actSupplier.openDeleteSupplierModal());
}

export function* deleteSupplier(request) {
  const {
    payload: { supplier, suppliers },
  } = request;
  clevertapInventorySuppliers.inventorySupplierDelete({ supplier });
  yield put(act.showProgressBar());
  try {
    const { isEnabled: isUsingReactorAPI } = yield select(
      unleashHasFeature('enable-supplier-bo-shadow-traffic-proxy')
    );

    const validUuid = checkIfValidUUID(supplier.id);
    const submittedId = validUuid ? 0 : supplier.id;
    const submittedParam = validUuid ? { supplierId: supplier.id } : {};

    yield call(
      isUsingReactorAPI
        ? apiInvSupplier.deleteReactorSupplier
        : apiInvSupplier.deleteSupplier,
      submittedId,
      supplier.name,
      submittedParam
    );

    yield put(actSupplier.closeRightColumn());
    yield put(actSupplier.setSupplierDetail(null));
    yield put(actSupplier.closeDeleteSupplierModal());
    // remove supplier from list of suppliers
    const newSuppliers: any[] = [];
    suppliers.forEach((s) => {
      if (s.id !== supplier.id) {
        newSuppliers.push(s);
      }
    });
    yield put(actSupplier.setSuppliers(newSuppliers));
    yield put(actNotif.success('Supplier successfully deleted.'));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* cancelDeleteSupplier() {
  yield put(actSupplier.closeDeleteSupplierModal());
}

export function* onClickCreateSupplier() {
  yield put(actSupplier.openRightColumn());
  yield put(actSupplier.setSupplierDetail(null));
  yield call(emptyForm);
}

export function* submitFormCreate(request) {
  const {
    payload: { data, previousSuppliers },
  } = request;
  clevertapInventorySuppliers.inventorySupplierCreate({
    supplier: data.supplier,
  });
  try {
    yield put(act.showProgressBar());
    yield put(actSupplier.isSubmittingForm(true));
    const { email } = data.supplier;
    const emailExist = yield call(
      validateEmailExist,
      email,
      (sup) => sup.email === email
    );

    if (emailExist) {
      // noinspection ExceptionCaughtLocallyJS
      throw new Error('Supplier email must be unique');
    }

    const { isEnabled: isUsingReactorAPI } = yield select(
      unleashHasFeature('enable-supplier-bo-shadow-traffic-proxy')
    );

    const supResponse = yield call(
      isUsingReactorAPI
        ? apiInvSupplier.postReactorSuppliers
        : apiInvSupplier.postSuppliers,
      data.supplier,
      {}
    );
    const supplier = supResponse.data;
    yield call(initializeForm, supplier);
    previousSuppliers.unshift(supplier);
    yield put(actSupplier.setSupplierDetail(supplier));
    yield put(actSupplier.setSuppliers(previousSuppliers));
    yield put(actNotif.success('Supplier successfully created.'));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(actSupplier.isSubmittingForm(false));
    yield put(act.hideProgressBar());
  }
}

export function* submitFormUpdate(request) {
  const {
    payload: { data, supplierId, previousSuppliers },
  } = request;
  try {
    yield put(act.showProgressBar());
    yield put(actSupplier.isSubmittingForm(true));
    const { email } = data.supplier;
    const emailExist = yield call(
      validateEmailExist,
      email,
      (sup) => sup.email === email && sup.id !== supplierId
    );

    if (emailExist) {
      // noinspection ExceptionCaughtLocallyJS
      throw new Error('Supplier email must be unique');
    }

    const { isEnabled: isUsingReactorAPI } = yield select(
      unleashHasFeature('enable-supplier-bo-shadow-traffic-proxy')
    );

    const validUuid = checkIfValidUUID(supplierId);
    const submittedId = validUuid ? 0 : supplierId;
    const submittedPayload = validUuid
      ? { ...data.supplier, id: 0 }
      : data.supplier;
    const submittedParam = validUuid ? { supplierId } : {};

    const supResponse = yield call(
      isUsingReactorAPI
        ? apiInvSupplier.putReactorSupplier
        : apiInvSupplier.putSupplier,
      submittedId,
      submittedPayload,
      submittedParam
    );
    const supplier = supResponse.data;
    yield call(initializeForm, supplier);
    const newSuppliers = previousSuppliers.map((s) => {
      if (s.id === supplier.id) {
        // return updated supplier
        return supplier;
      }
      return s;
    });
    yield put(actSupplier.setSupplierDetail(supplier));
    yield put(actSupplier.setSuppliers(newSuppliers));
    yield put(actNotif.success('Supplier successfully updated.'));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(actSupplier.isSubmittingForm(false));
  }
}

function checkIfValidUUID(str: string): boolean {
  // Regular expression to check if string is a valid UUID
  const regexExp =
    /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;

  return regexExp.test(str);
}

function* validateEmailExist(
  email: string,
  emailValidatorFunction: Function
): Generator<boolean> {
  // @ts-ignore
  const { isEnabled: isUsingPurchaseOrderAPI } = yield select(
    unleashHasFeature('enable-supplier-bo-read-purchase-order')
  );
  if (!isUsingPurchaseOrderAPI) {
    // check email uniqueness
    // @ts-ignore
    const checkEmailResponse = yield call(apiInvSupplier.getSuppliers, {
      keyword: email,
    });
    // @ts-ignore
    return checkEmailResponse?.data?.some(emailValidatorFunction);
  }
}

export default function* saga() {
  yield takeLatest(INVENTORY_SUPPLIER_PAGE_REQUEST, pageRequestFlow);
  yield takeLatest(INVENTORY_SUPPLIER_SUPPLIERS_REQUEST, suppliersRequest);
  yield takeEvery(INVENTORY_SUPPLIER_SUPPLIERS_LOAD_MORE, loadMoreSuppliers);
  yield takeLatest(INVENTORY_SUPPLIER_EXPORT_SUPPLIER, exportSupplier);
  yield takeLatest(INVENTORY_SUPPLIER_EXPORT_FAILED, exportSupplierFailed);
  yield takeLatest(INVENTORY_SUPPLIER_ON_CHANGE_KEYWORD, onChangeKeyword);
  yield takeLatest(INVENTORY_SUPPLIER_ON_CLICK_ROW, onClickRow);
  yield takeLatest(INVENTORY_SUPPLIER_ON_CLICK_CANCEL, onClickCancel);
  yield takeLatest(INVENTORY_SUPPLIER_ASK_DELETE_SUPPLIER, askDeleteSupplier);
  yield takeLatest(INVENTORY_SUPPLIER_DELETE_SUPPLIER, deleteSupplier);
  yield takeLatest(
    INVENTORY_SUPPLIER_CANCEL_DELETE_SUPPLIER,
    cancelDeleteSupplier
  );
  yield takeLatest(
    INVENTORY_SUPPLIER_ON_CLICK_CREATE_SUPPLIER,
    onClickCreateSupplier
  );
  yield takeLatest(INVENTORY_SUPPLIER_SUBMIT_FORM_CREATE, submitFormCreate);
  yield takeLatest(INVENTORY_SUPPLIER_SUBMIT_FORM_UPDATE, submitFormUpdate);
}
